import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import Panel from "../components/Panel";
import KpiCard from "../components/KpiCard";
import { fmtINR, fmtDate } from "../lib/format";
import { ArrowLeft } from "lucide-react";
import { ResponsiveContainer, RadialBarChart, RadialBar, PolarAngleAxis } from "recharts";

export default function ProjectDetail() {
    const { id } = useParams();
    const [p, setP] = useState(null);
    useEffect(() => { api.get(`/projects/${id}`).then((r) => setP(r.data)); }, [id]);
    if (!p) return <div className="font-mono-fin text-sm text-[var(--text-secondary)]">// LOADING PROJECT...</div>;

    const remainingCost = p.approved_budget - p.actual_cost;
    const overrun = p.completion_pct > 0 ? ((p.actual_cost / (p.approved_budget * p.completion_pct/100)) - 1) * 100 : 0;

    return (
        <div className="space-y-4">
            <Link to="/projects" data-testid="back-projects" className="inline-flex items-center gap-2 text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] hover:text-[var(--brand-amber)]">
                <ArrowLeft size={12} /> Back to Projects
            </Link>
            <div>
                <div className="font-mono-fin text-[10px] uppercase tracking-[0.25em] text-[var(--brand-amber)] mb-1">{p.code} · {p.location}</div>
                <h1 className="font-display font-black text-2xl md:text-3xl leading-tight">{p.name}</h1>
                <div className="font-mono-fin text-[11px] text-[var(--text-secondary)] uppercase tracking-wider mt-1">
                    Client: {p.client_name} · PM: {p.manager} · Duration: {fmtDate(p.start)} → {fmtDate(p.end)}
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KpiCard testId="pd-contract" label="Contract Value" value={p.contract_value} accent />
                <KpiCard testId="pd-actual" label="Actual Cost" value={p.actual_cost} sub={`Budget ${fmtINR(p.approved_budget)}`} />
                <KpiCard testId="pd-billed" label="Billed" value={p.billed} sub={`Collected ${fmtINR(p.collected)}`} />
                <KpiCard testId="pd-gp" label="Gross Profit" value={p.contract_value * p.gross_profit_pct / 100} sub={`${p.gross_profit_pct.toFixed(1)}% margin`} />
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
                <Panel testId="pd-health" title="Project Health" subtitle="EVM Metrics">
                    <div className="grid grid-cols-3 gap-2 text-center">
                        <div>
                            <div className="text-[9px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)]">Health</div>
                            <div className="font-mono-fin text-3xl font-bold" style={{ color: p.health >= 85 ? "#4af6c3" : p.health >= 70 ? "#fb8b1e" : "#ff433d" }}>{p.health}</div>
                        </div>
                        <div>
                            <div className="text-[9px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)]">SPI</div>
                            <div className={`font-mono-fin text-2xl ${p.spi >= 0.95 ? "text-pos" : "text-neg"}`}>{p.spi.toFixed(2)}</div>
                        </div>
                        <div>
                            <div className="text-[9px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)]">CPI</div>
                            <div className={`font-mono-fin text-2xl ${p.cpi >= 0.95 ? "text-pos" : "text-neg"}`}>{p.cpi.toFixed(2)}</div>
                        </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-panel space-y-2 text-[11px] font-mono-fin">
                        <div className="flex justify-between"><span className="text-[var(--text-secondary)] uppercase tracking-wider">Earned Value</span><span className="text-[var(--text-primary)]">{fmtINR(p.earned_value)}</span></div>
                        <div className="flex justify-between"><span className="text-[var(--text-secondary)] uppercase tracking-wider">Planned Value</span><span className="text-[var(--text-primary)]">{fmtINR(p.planned_value)}</span></div>
                        <div className="flex justify-between"><span className="text-[var(--text-secondary)] uppercase tracking-wider">Remaining Budget</span><span className={remainingCost > 0 ? "text-pos" : "text-neg"}>{fmtINR(remainingCost)}</span></div>
                        <div className="flex justify-between"><span className="text-[var(--text-secondary)] uppercase tracking-wider">Cost Overrun</span><span className={overrun > 0 ? "text-neg" : "text-pos"}>{overrun.toFixed(1)}%</span></div>
                    </div>
                </Panel>

                <Panel testId="pd-progress" className="xl:col-span-2" title="Completion" subtitle={`${p.status} · ${p.risk} Risk`}>
                    <div className="mb-4">
                        <div className="flex justify-between text-xs font-mono-fin uppercase tracking-wider mb-2">
                            <span className="text-[var(--text-secondary)]">Progress</span>
                            <span className="text-[var(--text-primary)]">{p.completion_pct.toFixed(1)}%</span>
                        </div>
                        <div className="h-3 bg-[var(--bg-base)] border border-panel">
                            <div className="h-full bg-[var(--brand-amber)]" style={{ width: `${p.completion_pct}%` }} />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                        <div>
                            <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)]">Retention</div>
                            <div className="font-mono-fin text-lg text-amber">{fmtINR(p.retention)}</div>
                        </div>
                        <div>
                            <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)]">Yet to Bill</div>
                            <div className="font-mono-fin text-lg text-[var(--text-primary)]">{fmtINR(p.contract_value - p.billed)}</div>
                        </div>
                        <div>
                            <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)]">Labour on-site</div>
                            <div className="font-mono-fin text-lg text-[var(--text-primary)]">{p.labour?.count || 0}</div>
                        </div>
                        <div>
                            <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)]">Equipment</div>
                            <div className="font-mono-fin text-lg text-[var(--text-primary)]">{p.equipment.length}</div>
                        </div>
                    </div>
                </Panel>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                <Panel testId="pd-receivables" title="Receivables from Client">
                    {p.receivables.length === 0 ? (
                        <div className="text-xs text-[var(--text-secondary)] font-mono-fin">No open receivables.</div>
                    ) : (
                        <table className="w-full font-mono-fin text-xs">
                            <thead className="border-b border-panel">
                                <tr className="text-[10px] uppercase tracking-wider text-[var(--text-secondary)]">
                                    <th className="text-left py-2">Invoice</th>
                                    <th className="text-right">Amount</th>
                                    <th className="text-right">Due</th>
                                    <th className="text-left">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {p.receivables.map((r) => (
                                    <tr key={r.id} className="border-b border-[var(--border)]/50">
                                        <td className="py-2 text-[var(--text-primary)]">{r.invoice_no}</td>
                                        <td className="text-right text-amber">{fmtINR(r.outstanding)}</td>
                                        <td className="text-right text-[var(--text-secondary)]">{fmtDate(r.due_date)}</td>
                                        <td className="text-[10px] uppercase tracking-wider text-[var(--text-secondary)]">{r.status}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </Panel>

                <Panel testId="pd-equipment" title="Deployed Equipment">
                    {p.equipment.length === 0 ? (
                        <div className="text-xs text-[var(--text-secondary)] font-mono-fin">No equipment deployed.</div>
                    ) : (
                        <table className="w-full font-mono-fin text-xs">
                            <thead className="border-b border-panel">
                                <tr className="text-[10px] uppercase tracking-wider text-[var(--text-secondary)]">
                                    <th className="text-left py-2">Asset</th>
                                    <th className="text-right">Utilization</th>
                                    <th className="text-right">Cost/hr</th>
                                </tr>
                            </thead>
                            <tbody>
                                {p.equipment.map((e) => (
                                    <tr key={e.id} className="border-b border-[var(--border)]/50">
                                        <td className="py-2 text-[var(--text-primary)]">{e.name}</td>
                                        <td className={`text-right ${e.utilization_pct >= 70 ? "text-pos" : e.utilization_pct >= 50 ? "text-amber" : "text-neg"}`}>{e.utilization_pct}%</td>
                                        <td className="text-right text-[var(--text-primary)]">₹{e.cost_per_hr}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </Panel>
            </div>
        </div>
    );
}
